import {
  deriveAnnotationCamera,
  Vec3,
  type CameraLike,
  type GizmoMode,
  type SampleAnimTrackRouteOptions,
  type Vec3Like,
  type ViewerAnimTrack,
  type ViewerAnnotation,
  type ViewerSceneApi,
} from '@mindcloudxai/shared';
import { Entity, Gizmo, TranslateGizmo, RotateGizmo } from 'playcanvas';
import type { Annotation as SettingsAnnotation } from '../../../submodules/supersplat-viewer/src/settings';
import type { ViewerContext } from '../types';
import { getCamera } from './camera';
import { TrackRouteOverlay } from '../track-route-overlay';

export function createSceneApi(getCtx: () => ViewerContext): ViewerSceneApi {
  let routeOverlay: TrackRouteOverlay | null = null;

  // 相机 gizmo 状态：独立于模型 gizmo，用于运镜关键帧位置编辑
  let cameraGizmo: TranslateGizmo | RotateGizmo | null = null;
  let cameraGizmoPivot: Entity | null = null;
  let cameraGizmoBusy = false;
  // 使用 any 规避 playcanvas 双实例 Layer 类型不兼容（同 model.ts）
  let cameraGizmoLayer: any = null;

  function isLayerInScene(ctx: ViewerContext, layer: any): boolean {
    const layers = ctx.app.scene.layers as any;
    return layers.getLayerById?.(layer.id) === layer || layers.layerList?.includes(layer);
  }

  function ensureCameraGizmoLayer(
    ctx: ViewerContext,
    cameraComp: NonNullable<ViewerContext['camera']['camera']>,
  ) {
    const layers = ctx.app.scene.layers as any;
    const existing = ctx.app.scene.layers.getLayerByName('Gizmo') as any;
    const cached =
      cameraGizmoLayer && isLayerInScene(ctx, cameraGizmoLayer) ? cameraGizmoLayer : null;
    const layer =
      cameraGizmo && cameraGizmoLayer
        ? cameraGizmoLayer
        : (existing ?? cached ?? cameraGizmoLayer ?? (Gizmo.createLayer(ctx.app as any) as any));
    cameraGizmoLayer = layer;
    layer.enabled = true;
    if (!isLayerInScene(ctx, layer)) {
      layers.insert(layer, layers.layerList.length);
    }
    cameraComp.layers = [...cameraComp.layers.filter((id) => id !== layer.id), layer.id];
    return layer;
  }

  function getCameraGizmoMeshInstances() {
    const gizmo: any = cameraGizmo;
    return (Object.values(gizmo?._shapes ?? {}) as any[]).flatMap(
      (shape) => shape.meshInstances ?? [],
    );
  }

  function refreshCameraGizmoShapeLayers() {
    const layer = cameraGizmoLayer;
    const layerId = layer?.id;
    if (layerId === undefined) return;
    const gizmo: any = cameraGizmo;
    const shapeEntries = Object.values(gizmo?._shapes ?? {}) as any[];
    const meshInstances: any[] = [];
    shapeEntries.forEach((shape) => {
      const render = shape.entity?.render;
      if (render) {
        render.layers = [layerId];
        render.enabled = true;
        shape.entity.enabled = true;
      }
      (shape.meshInstances ?? []).forEach((meshInstance: any) => {
        meshInstance.layer = layerId;
        meshInstances.push(meshInstance);
      });
    });
    if (meshInstances.length > 0) {
      layer.addMeshInstances?.(meshInstances, true);
    }
  }

  function removeCameraGizmoShapeLayers() {
    const layer = cameraGizmoLayer;
    const meshInstances = getCameraGizmoMeshInstances();
    if (meshInstances.length > 0) {
      layer?.removeMeshInstances?.(meshInstances, true);
    }
  }

  function destroyCameraGizmo() {
    const ctx = getCtx();
    if (cameraGizmoBusy) {
      ctx.global?.events.fire('model.gizmoBusy', false);
      cameraGizmoBusy = false;
    }
    if (cameraGizmo) {
      // 先从层中移除 mesh instances，避免 destroy 后层内残留过期引用
      removeCameraGizmoShapeLayers();
      cameraGizmo.detach();
      cameraGizmo.destroy();
      cameraGizmo = null;
    }
    if (cameraGizmoPivot) {
      cameraGizmoPivot.destroy();
      cameraGizmoPivot = null;
    }
  }

  function setCameraGizmo(pose: CameraLike | null, mode: GizmoMode = 'translate'): void {
    const ctx = getCtx();

    // pose 为 null → 移除 gizmo
    if (!pose) {
      destroyCameraGizmo();
      ctx.app.renderNextFrame = true;
      return;
    }

    // 销毁旧 gizmo（模式或位置变化时重建）
    destroyCameraGizmo();

    const cameraComp = ctx.camera.camera;
    if (!cameraComp) return;

    // 确保 gizmo 渲染层存在并已加入场景层列表
    cameraGizmoLayer = ensureCameraGizmoLayer(ctx, cameraComp);

    // 在关键帧相机位置创建 pivot
    cameraGizmoPivot = new Entity('cameraGizmoPivot');
    (ctx.app.root as any).addChild(cameraGizmoPivot);
    cameraGizmoPivot.setLocalPosition(pose.position.x, pose.position.y, pose.position.z);

    if (mode === 'translate') {
      cameraGizmo = new TranslateGizmo(cameraComp, cameraGizmoLayer);
    } else {
      cameraGizmo = new RotateGizmo(cameraComp, cameraGizmoLayer);
    }
    // 调整 gizmo 大小
    const canvas = ctx.canvas;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    const m = Math.max(w, h);
    if (m > 0) {
      cameraGizmo.size = 1200 / m;
    }

    // gizmo 拖拽事件：通知上层相机位置已更新
    cameraGizmo.on('render:update', () => {
      ctx.app.renderNextFrame = true;
    });
    cameraGizmo.on('transform:start', () => {
      // 拖拽期间屏蔽相机输入
      cameraGizmoBusy = true;
      ctx.global?.events.fire('model.gizmoBusy', true);
    });
    cameraGizmo.on('transform:move', () => {
      if (!cameraGizmoPivot) return;
      const pos = cameraGizmoPivot.getLocalPosition();
      ctx.events.fire('cameraGizmo:transform', { x: pos.x, y: pos.y, z: pos.z });
    });
    cameraGizmo.on('transform:end', () => {
      cameraGizmoBusy = false;
      ctx.global?.events.fire('model.gizmoBusy', false);
      if (cameraGizmoPivot) {
        const pos = cameraGizmoPivot.getLocalPosition();
        ctx.events.fire('cameraGizmo:transform', { x: pos.x, y: pos.y, z: pos.z });
      }
    });

    cameraGizmo.attach(cameraGizmoPivot);
    // PlayCanvas Gizmo 可能在 attach 当帧把 root.enabled 置为 false，后续 update 会直接返回。
    cameraGizmo.root.enabled = true;
    // attach 后再刷新 shape layers，避免 attach 内部重新赋值覆盖层设置
    refreshCameraGizmoShapeLayers();
    cameraGizmo.update();
    ctx.app.renderNextFrame = true;
  }

  return {
    getAnnotations: () => getCtx().annotations,
    focusAnnotation: (index) => focusAnnotation(getCtx(), index),
    showTrackRoute: (points: Vec3Like[] | null) => {
      const ctx = getCtx();
      if (!points) {
        routeOverlay?.dispose();
        routeOverlay = null;
        return;
      }
      if (!routeOverlay) {
        routeOverlay = new TrackRouteOverlay(ctx.app);
      }
      routeOverlay.update(points);
    },
    showCameraTrajectory: (poses: CameraLike[] | null) => {
      const ctx = getCtx();
      if (!poses) {
        routeOverlay?.dispose();
        routeOverlay = null;
        return;
      }
      if (!routeOverlay) {
        routeOverlay = new TrackRouteOverlay(ctx.app);
      }
      routeOverlay.updateCameraPoses(poses);
    },
    showAnimTrackRoute: (track: ViewerAnimTrack | null, options?: SampleAnimTrackRouteOptions) => {
      const ctx = getCtx();
      if (!track) {
        routeOverlay?.dispose();
        routeOverlay = null;
        return;
      }
      if (!routeOverlay) {
        routeOverlay = new TrackRouteOverlay(ctx.app);
      }
      routeOverlay.updateAnimTrack(track, options);
    },
    showPreviewCameraPose: (pose: CameraLike | null) => {
      const ctx = getCtx();
      if (!pose && !routeOverlay) return;
      if (!routeOverlay) {
        routeOverlay = new TrackRouteOverlay(ctx.app);
      }
      routeOverlay.updatePreviewCameraPose(pose);
    },
    setCameraGizmo,
  };
}

function focusAnnotation(ctx: ViewerContext, index: number): void {
  if (!ctx.global || !ctx.viewer?.cameraManager) return;
  const annotation: ViewerAnnotation | undefined = ctx.annotations[index];
  if (!annotation) return;

  // 基于当前相机推导机位，再复用 CameraManager annotation.activate 的平滑过渡
  const camera = deriveAnnotationCamera(annotation, getCamera(ctx));
  const anchor = Vec3.from(annotation.position);
  const payload: SettingsAnnotation = {
    position: [anchor.x, anchor.y, anchor.z],
    title: annotation.title,
    text: annotation.text ?? '',
    camera: {
      initial: {
        position: [camera.position.x, camera.position.y, camera.position.z],
        target: [camera.target.x, camera.target.y, camera.target.z],
        fov: camera.fov ?? ctx.camera.camera?.fov ?? 75,
      },
    },
  } as SettingsAnnotation;
  ctx.global.events.fire('annotation.activate', payload);
  // 同步对外触发 viewer 契约事件，业务侧据此联动 UI 高亮/弹窗
  ctx.events.fire('annotation:activate', index);
  ctx.app.renderNextFrame = true;
}
